import * as Flows from './types.no-deps'
import { andThen, filter } from '../../lib/functional/generator.no-deps'
import { hasShowIfs, isSectionElement } from './typeGuards.no-deps'
import { pipe } from '../../lib/functional/pipe.no-deps'
import { compose } from '../../lib/functional/compose.no-deps'
import type { ElementOrNested } from './element.no-deps'

export interface TraverseOptions {
    traverseLoops?: boolean
}

/**
 * Given a flow section, will iterate over both the direct child steps
 * and steps in nested loops, e.g.
 *
 *    Given:
 *    Section 1
 *      - Step 1.1
 *      - Step 1.2
 *         - Section 1.2.1
 *             - Step 1.2.1.1
 *             - Step 1.2.1.2
 *      - Step 1.3
 *          - Element (Loop) 1.3.1
 *              - Step 1.3.1.1
 *
 *    It will yield:
 *    Step 1.1, Step 1.2, Step 1.3, Step 1.3.1.1
 */
export function sectionsSteps(opts?: TraverseOptions) {
    return function* (section: Flows.HasSteps): Generator<Flows.Step> {
        for (const step of section.steps) {
            yield step
            if (opts?.traverseLoops === false) continue
            yield* stepsNestedLoopSteps(step)
        }
    }
}

export function* stepsNestedLoopSteps(step: Flows.Step): Generator<Flows.Step> {
    if (!Flows.isConcreteStep(step)) {
        return
    }

    for (const element of step.elements) {
        if (element.type === 'loop') {
            for (const subStep of element.steps) {
                yield subStep
                yield* stepsNestedLoopSteps(subStep)
            }
        }
    }
}

/**
 * Given a flow step, will iterate over both direct child elements,
 * and elements in nested show ifs elements, e.g.
 *
 *    Given:
 *    Step 1
 *      - Element 1.1
 *      - Element 1.2
 *          - ShowIf 1.2.1
 *              - NestedElement 1.2.1.1
 *              - NestedElement 1.2.1.2
 *          - ShowIf 1.2.2
 *              - NestedElement 1.2.2.1
 *      - Element (Section) 1.3
 *          - Step 1.3.1
 *              - Element 1.3.1.1
 *              - Element 1.3.1.2
 *      - Element 1.4
 *
 *    It will yield:
 *    Element 1.1, Element 1.2, NestedElement 1.2.1.1, NestedElement 1.2.1.2,
 *    NestedElement 1.2.2.1, Element 1.3, Element 1.4
 *
 *    It will not yield:
 *    Element 1.3.1.1, Element 1.3.1.2
 */
export function* stepsElements(step: Flows.Step): Generator<Flows.Element | Flows.NestedElement> {
    if (!Flows.isConcreteStep(step)) {
        return
    }

    for (const element of step.elements) {
        yield element

        if (!hasShowIfs(element) || !element.showIfs) continue

        for (const showIf of element.showIfs) {
            for (const nestedElement of showIf.elements) {
                yield nestedElement
            }
        }
    }
}

/**
 * Given a flow section, will iterator over elements in direct child steps,
 * including nested show-if elements and elements in loops
 *
 *    Given:
 *    Section 1
 *      - Step 1.1
 *          - Element 1.1.1
 *          - Element 1.1.2
 *              - ShowIf 1.1.2.1
 *                  - NestedElement 1.1.2.1.1
 *                  - NestedElement 1.1.2.1.2
 *              - ShowIf 1.1.2.2
 *                  - NestedElement 1.1.2.2.1
 *      - Step 1.2
 *         - Element (Section) 1.2.1
 *             - Step 1.2.1.1
 *                 - Element 1.2.1.1.1
 *             - Step 1.2.1.2
 *                 - Element (Section) 1.2.1.2.1
 *                     - Step 1.2.1.2.1.1
 *                         - Element 1.2.1.2.1.1.1
 *             - Step 1.2.1.3
 *                 - Element 1.2.1.3.1
 *      - Step 1.3
 *          - Element 1.3.1
 *
 *    It will yield:
 *    Element 1.1.1, Element 1.1.2, NestedElement 1.1.2.1.1, NestedElement 1.1.2.1.2, NestedElement 1.1.2.2.1,
 *    Element 1.2.1, Element 1.3.1
 *
 *    It will not yield:
 *    Element 1.2.1.1.1, Element 1.2.1.2.1, Element 1.2.1.2.1.1.1, Element 1.2.1.3.1
 */
export const sectionsElements = (opts?: TraverseOptions) => compose(sectionsSteps(opts), andThen(stepsElements))

/**
 * Given a flow section, will recursively walk over all subsections including itself, e.g
 *
 *    Given:
 *    Section 1
 *      - Step 1.1
 *      - Step 1.2
 *         - Section 1.2.1
 *             - Step 1.2.1.1
 *             - Step 1.2.1.2
 *                 - Section 1.2.1.2.1
 *      - Step 1.3
 *
 *    It will yield:
 *    Section 1, Section 1.2.1, Section 1.2.1.2.1
 */
export function walkSections(opts?: TraverseOptions) {
    return function* <S extends Flows.HasSteps>(section: S): Generator<S | Flows.SectionElement> {
        yield section
        yield* pipe(section, sectionsElements(opts), filter(isSectionElement), andThen(walkSections(opts)))
    }
}

/**
 * Given a flow section, will recursively walk over all steps, one section at a time, e.g
 *
 *    Given:
 *    Section 1
 *      - Step 1.1
 *      - Step 1.2
 *         - Section 1.2.1
 *             - Step 1.2.1.1
 *             - Step 1.2.1.2
 *                 - Section 1.2.1.2.1
 *                     - Step 1.2.1.2.1.1
 *             - Step 1.2.1.3
 *      - Step 1.3
 *
 *    It will yield:
 *    Step 1.1, Step 1.2, Step 1.3, Step 1.2.1.1, Step 1.2.1.2, Step 1.2.1.3, Step 1.2.1.2.1.1
 */
export const walkSteps = (opts?: TraverseOptions) => compose(walkSections(opts), andThen(sectionsSteps(opts)))

/**
 * Given a flow step, will recursively walk over all steps, including the root step
 *
 *    Given:
 *    Step 1
 *      - Section 1.1
 *          - Step 1.1.1
 *          - Step 1.1.2
 *      - Section 1.2
 *          - Step 1.2.1
 *
 *    It will yield:
 *    Step 1, Step 1.1.1, Step 1.1.2, Step 1.2.1
 */
export function walkStepsOfStep(opts?: TraverseOptions) {
    return function* (step: Flows.Step): Generator<Flows.Step> {
        yield step

        if (!Flows.isConcreteStep(step)) {
            return
        }

        for (const element of step.elements) {
            if (element.type !== 'loop' && element.type !== 'section') {
                continue
            }

            yield* walkSteps(opts)(element)
        }
    }
}

/**
 * Given a flow section, will recursively walk over all elements, including nested show-if elements,
 * one section at a time, e.g
 *
 *    Given:
 *    Section 1
 *      - Step 1.1
 *          - Element 1.1.1
 *          - Element 1.1.2
 *              - ShowIf 1.1.2.1
 *                  - NestedElement 1.1.2.1.1
 *                  - NestedElement 1.1.2.1.2
 *              - ShowIf 1.1.2.2
 *                  - NestedElement 1.1.2.2.1
 *      - Step 1.2
 *         - Element (Section) 1.2.1
 *             - Step 1.2.1.1
 *                 - Element 1.2.1.1.1
 *             - Step 1.2.1.2
 *                 - Element (Section) 1.2.1.2.1
 *                     - Step 1.2.1.2.1.1
 *                         - Element 1.2.1.2.1.1.1
 *             - Step 1.2.1.3
 *                 - Element 1.2.1.3.1
 *      - Step 1.3
 *          - Element 1.3.1
 *
 *    It will yield:
 *    Element 1.1.1, Element 1.1.2, NestedElement 1.1.2.1.1, NestedElement 1.1.2.1.2, NestedElement 1.1.2.2.1,
 *    Element 1.2.1, Element 1.3.1, Element 1.2.1.1, Element 1.2.1.1.1, Element 1.2.1.2.1, Element 1.2.1.3.1,
 *    Element 1.2.1.2.1.1.1
 */
export const walkElements = (opts?: TraverseOptions) => compose(walkSteps(opts), andThen(stepsElements))

/**
 * Given a flow section, will recursively yield all elements, including nested show-if elements,
 * in the order they appear in the flow.
 *
 * This is similar to `walkElements`, but ensures that an element nested inside a section or
 * step will be yielded before later siblings of the section or step, e.g
 *
 *    Given:
 *    Section 1
 *      - Step 1.1
 *          - Element 1.1.1
 *          - Element 1.1.2
 *              - ShowIf 1.1.2.1
 *                  - NestedElement 1.1.2.1.1
 *                  - NestedElement 1.1.2.1.2
 *              - ShowIf 1.1.2.2
 *                  - NestedElement 1.1.2.2.1
 *      - Step 1.2
 *         - Element (Section) 1.2.1
 *             - Step 1.2.1.1
 *                 - Element 1.2.1.1.1
 *             - Step 1.2.1.2
 *                 - Element (Section) 1.2.1.2.1
 *                     - Step 1.2.1.2.1.1
 *                         - Element 1.2.1.2.1.1.1
 *             - Step 1.2.1.3
 *                 - Element 1.2.1.3.1
 *      - Step 1.3
 *          - Element 1.3.1
 *
 *    It will yield:
 *    Element 1.1.1, Element 1.1.2, NestedElement 1.1.2.1.1, NestedElement 1.1.2.1.2, NestedElement 1.1.2.2.1,
 *    Element 1.2.1, Element 1.2.1.1.1, Element 1.2.1.2.1, Element 1.2.1.2.1.1.1, Element 1.2.1.3.1, Element 1.3.1,
 */
export const walkElementsOrderedByDisplay = (opts?: TraverseOptions) =>
    function* (section: Flows.HasSteps): Generator<ElementOrNested> {
        for (const element of sectionsElements(opts)(section)) {
            yield element
            if (element.type === 'section') {
                yield* walkElementsOrderedByDisplay(opts)(element)
            }
        }
    }
